// Events Capture — settings page for per-site capture toggles that
// drive the Phase 4a pingora data plane.
//
// Backed by the CP endpoints shipped in feat/asec-events-capture-config-cp:
//   GET /api/user/v1/asec/:site_uid/events_capture
//   PUT /api/user/v1/asec/:site_uid/events_capture
//
// The first GET on a site that has never been configured returns the
// safe-default envelope (enabled=false + all sub-toggles false), so
// operators must explicitly opt in.
//
// This file is a redesign of the original Phase 5b screen against the
// "Investigation depth" mock at /home/ubuntu/events-capture-design.
// Key UX upgrades over the previous version:
//   - Master toggle row is elevated + accent-tinted when on.
//   - Six sub-toggles live in a "Captured fields" group connected by a
//     left rail, with an "N of 6 on" counter and a smooth collapse
//     transition when the master switches off.
//   - Icon-led rows give each option a scannable identity.
//   - Body sample size cap is a stepper (- value +) instead of a
//     freeform number field.
//   - The save/reset controls live in an in-card footer with a
//     "Last updated" stamp — replaces the floating StickySaveBar
//     since the sheet is the only thing on the page.
import React from "react";
import {
  Box, Stack, Typography, IconButton, CircularProgress,
} from "@mui/material";
import {
  ShieldAlert, DatabaseZap, ArrowUpRight, ArrowDownLeft,
  FileUp, FileDown, Braces, FileSearch, Ruler, History,
  Minus, Plus,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import AsecPage, { SectionTitle } from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { GreenSwitch } from "../../../../common/Greens";
import StickySaveBar from "../../../../common/StickySaveBar";
import { collectObjectDiff, enabledLabel } from "../../../../common/stickySaveBarUtils";
import { asecContentCardSx } from "./asecUi";

// ── Defaults must match the CP `defaults` shape so a never-saved
// site sees the same "all off" state as a freshly inserted row.
const DEFAULTS = Object.freeze({
  enabled:               false,
  request_headers:       false,
  response_headers:      false,
  request_body_sample:   false,
  response_body_sample:  false,
  graphql_variables:     false,
  spec_actual_value:     false,
  body_sample_cap_bytes: 4096,
});

const CAP_MIN = 256;
const CAP_MAX = 8192;
const CAP_STEP = 256;
const CAP_DEFAULT = 4096;

const SUB_FIELDS = [
  {
    key: "request_headers", Icon: ArrowUpRight,
    title: "Capture request headers",
    desc: "Stores DLP-redacted request headers on every API event row.",
  },
  {
    key: "response_headers", Icon: ArrowDownLeft,
    title: "Capture response headers",
    desc: "Stores DLP-redacted response headers on every API event row.",
  },
  {
    key: "request_body_sample", Icon: FileUp,
    title: "Capture request body sample",
    desc: "Stores a truncated DLP-scrubbed request body (capped by the size below).",
  },
  {
    key: "response_body_sample", Icon: FileDown,
    title: "Capture response body sample",
    desc: "Stores a truncated DLP-scrubbed response body (capped by the size below).",
  },
  {
    key: "graphql_variables", Icon: Braces,
    title: "Capture GraphQL variables",
    desc: "Stores the variables payload of GraphQL operations (graphql-events only).",
  },
  {
    key: "spec_actual_value", Icon: FileSearch,
    title: "Capture spec actual values",
    desc: "Stores the offending value and expected type on each spec-validation failure.",
  },
];

const EVENTS_CAPTURE_DIFF_FIELDS = [
  { key: "enabled", label: "Investigation depth", format: enabledLabel },
  ...SUB_FIELDS.map(({ key, title }) => ({
    key,
    label: title.replace(/^Capture /, ""),
    format: enabledLabel,
  })),
  { key: "body_sample_cap_bytes", label: "Body sample cap", format: (v) => `${v} bytes` },
];

const ACCENT = colors.primary[300];

function Stepper({ value, min, max, step, onChange, disabled }) {
  const clamp = (v) => Math.max(min, Math.min(max, v));
  const btnSx = {
    width: 34, height: 38, minWidth: 34,
    p: 0, borderRadius: 0,
    color: colors.neutral[700],
    "&:hover": { bgcolor: colors.neutral[100], color: colors.neutral[1000] },
  };
  return (
    <Stack direction="row" alignItems="center" sx={{
      height: 38,
      border: `1px solid ${colors.neutral[300]}`,
      borderRadius: "8px",
      overflow: "hidden",
      bgcolor: "#fff",
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? "none" : "auto",
    }}>
      <IconButton size="small" sx={btnSx} onClick={() => onChange(clamp(value - step))} aria-label="Decrease">
        <Minus size={15} />
      </IconButton>
      <Box
        component="input"
        type="text"
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const n = parseInt(String(e.target.value).replace(/[^0-9]/g, ""), 10);
          onChange(Number.isNaN(n) ? min : clamp(n));
        }}
        sx={{
          width: 64, height: "100%",
          border: "none",
          borderLeft: `1px solid ${colors.neutral[200]}`,
          borderRight: `1px solid ${colors.neutral[200]}`,
          textAlign: "center",
          fontSize: 14,
          color: colors.neutral[1000],
          background: "transparent",
          "&:focus": { outline: "none", bgcolor: colors.neutral[50] },
        }}
      />
      <IconButton size="small" sx={btnSx} onClick={() => onChange(clamp(value + step))} aria-label="Increase">
        <Plus size={15} />
      </IconButton>
    </Stack>
  );
}

function Callout() {
  const codeSx = {
    fontSize: 12,
    color: colors.primary[400],
    bgcolor: `${colors.primary[300]}17`,  // ~9%
    px: 0.625, py: "1px",
    borderRadius: "4px",
    whiteSpace: "nowrap",
  };
  return (
    <Stack direction="row" spacing={1.5} sx={{
      mx: 3.5, mb: 2,
      p: "14px 16px",
      borderRadius: "8px",
      bgcolor: "rgba(2,85,237,0.07)",
      border: `1px solid ${colors.primary[300]}29`,  // 16%
      alignItems: "flex-start",
    }}>
      <Box sx={{ pt: "1px", color: colors.primary[300], flex: "0 0 auto" }}>
        <ShieldAlert size={17} />
      </Box>
      <Typography sx={{
        fontSize: 14, lineHeight: "20px", color: colors.neutral[800],
      }}>
        Captured headers and bodies are PII-redacted at write time using your DLP
        policy. The{" "}
        <Box component="code" sx={codeSx}>Authorization</Box>,{" "}
        <Box component="code" sx={codeSx}>Cookie</Box>,{" "}
        <Box component="code" sx={codeSx}>Set-Cookie</Box>,{" "}
        <Box component="code" sx={codeSx}>Proxy-Authorization</Box>,{" "}
        <Box component="code" sx={codeSx}>X-API-Key</Box>,{" "}
        <Box component="code" sx={codeSx}>X-Auth-Token</Box>, and{" "}
        <Box component="code" sx={codeSx}>X-CSRF-Token</Box> headers are dropped
        wholesale — not redacted — regardless of DLP policy. Enabling capture
        increases ClickHouse storage proportional to request volume.
      </Typography>
    </Stack>
  );
}

function MasterRow({ on, onChange, disabled }) {
  return (
    <Box
      sx={{
        mx: 3.5, mt: 2.75,
        display: "grid",
        gridTemplateColumns: "40px 1fr auto",
        gap: 1.75,
        alignItems: "center",
        p: "16px 18px",
        borderRadius: "8px",
        border: `1px solid ${on ? `${ACCENT}61` /* 38% */ : colors.neutral[300]}`,
        bgcolor: on ? `${ACCENT}0D` /* ~5% */ : colors.neutral[50],
        transition: "border-color 0.2s, background 0.2s",
      }}
    >
      <Box sx={{
        width: 40, height: 40, borderRadius: "8px",
        display: "grid", placeItems: "center",
        bgcolor: on ? ACCENT : "#fff",
        border: `1px solid ${on ? ACCENT : colors.neutral[300]}`,
        color: on ? "#fff" : colors.neutral[700],
        transition: "all 0.2s",
      }}>
        <DatabaseZap size={20} />
      </Box>
      <Box>
        <Typography sx={{
          fontSize: 16, fontWeight: 600,
          color: colors.neutral[1000], mb: "3px",
        }}>
          Enable enriched event capture
        </Typography>
        <Typography sx={{
          fontSize: 12, lineHeight: "18px",
          color: colors.neutral[600], maxWidth: "58ch",
        }}>
          When off, only the existing summary columns are written. When on, the
          options below control which extra columns are populated.
        </Typography>
      </Box>
      <GreenSwitch checked={on} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
    </Box>
  );
}

function FieldRow({ field, on, onChange, disabled }) {
  const Icon = field.Icon;
  const isOff = disabled;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "34px 1fr auto",
        gap: "13px",
        alignItems: "center",
        p: "14px 12px",
        borderBottom: `1px solid ${colors.neutral[200]}`,
        borderRadius: "8px",
        opacity: isOff ? 0.45 : 1,
        transition: "opacity 0.25s, background 0.15s",
        "&:hover": { bgcolor: isOff ? "transparent" : colors.neutral[50] },
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{
        width: 34, height: 34, borderRadius: "8px",
        display: "grid", placeItems: "center",
        bgcolor: colors.primary.alpha || `${colors.primary[300]}1A`,
        color: colors.primary[300],
      }}>
        <Icon size={17} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: 14, fontWeight: 500,
          color: colors.neutral[1000], mb: "2px",
        }}>
          {field.title}
        </Typography>
        <Typography sx={{
          fontSize: 12, lineHeight: "18px",
          color: colors.neutral[600], maxWidth: "58ch",
        }}>
          {field.desc}
        </Typography>
      </Box>
      <GreenSwitch
        checked={on && !disabled}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
    </Box>
  );
}

function CapRow({ value, onChange, disabled }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "34px 1fr auto",
        gap: "13px",
        alignItems: "center",
        mt: "14px",
        p: "16px",
        borderRadius: "8px",
        bgcolor: colors.neutral[50],
        border: `1px solid ${colors.neutral[200]}`,
        opacity: disabled ? 0.45 : 1,
        transition: "opacity 0.25s",
      }}
    >
      <Box sx={{
        width: 34, height: 34, borderRadius: "8px",
        display: "grid", placeItems: "center",
        bgcolor: colors.primary.alpha || `${colors.primary[300]}1A`,
        color: colors.primary[300],
      }}>
        <Ruler size={17} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: 14, fontWeight: 500,
          color: colors.neutral[1000], mb: "2px",
        }}>
          Body sample size cap
        </Typography>
        <Typography sx={{
          fontSize: 12, lineHeight: "18px",
          color: colors.neutral[600], maxWidth: "58ch",
        }}>
          Maximum bytes pingora stores from any single request or response body.
          Range {CAP_MIN}–{CAP_MAX}. Default {CAP_DEFAULT}.
        </Typography>
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Stepper
          value={value} min={CAP_MIN} max={CAP_MAX} step={CAP_STEP}
          onChange={onChange} disabled={disabled}
        />
        <Typography sx={{ fontSize: 14, color: colors.neutral[600] }}>
          bytes
        </Typography>
      </Stack>
    </Box>
  );
}

function CapturedFieldsGroup({ master, fields, patch, disabled }) {
  const enabledCount = SUB_FIELDS.reduce((n, f) => n + (fields[f.key] ? 1 : 0), 0);
  const showGroup = master;
  return (
    <Box
      aria-hidden={!showGroup}
      sx={{
        mx: 3.5, mt: 1,
        display: "grid",
        gridTemplateRows: showGroup ? "1fr" : "0fr",
        opacity: showGroup ? 1 : 0,
        marginTop: showGroup ? "8px" : 0,
        pointerEvents: showGroup ? "auto" : "none",
        transition: "opacity 0.3s ease, grid-template-rows 0.35s ease, margin 0.3s ease",
      }}
    >
      <Box sx={{ overflow: "hidden", pt: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ p: "8px 2px 12px" }}>
          <Typography sx={{
                        fontSize: 12, fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: colors.neutral[600],
            whiteSpace: "nowrap",
          }}>
            Captured fields
          </Typography>
          <Typography sx={{
            fontSize: 12, color: colors.neutral[600], whiteSpace: "nowrap",
          }}>
            {showGroup ? `${enabledCount} of ${SUB_FIELDS.length} on` : "Disabled"}
          </Typography>
        </Stack>
        <Box sx={{
          borderLeft: `2px solid ${colors.neutral[200]}`,
          pl: 2,
          ml: 2.25,
          display: "flex",
          flexDirection: "column",
        }}>
          {SUB_FIELDS.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              on={fields[f.key]}
              disabled={disabled || !master}
              onChange={(v) => patch({ [f.key]: v })}
            />
          ))}
          <CapRow
            value={Number.isFinite(Number(fields.body_sample_cap_bytes))
              ? Number(fields.body_sample_cap_bytes) : CAP_DEFAULT}
            onChange={(v) => patch({ body_sample_cap_bytes: v })}
            disabled={disabled || !master}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ── Body ────────────────────────────────────────────────────────

function Body({ siteId }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure } = useAsecPermissions();

  const [baseline, setBaseline] = React.useState(null);
  const [draft, setDraft]       = React.useState(null);
  const [isApplying, setIsApplying] = React.useState(false);

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const r = await asec.eventsCaptureGet(siteId);
      const next = { ...DEFAULTS, ...(r?.data || {}) };
      setBaseline(next);
      setDraft(next);
    } catch (e) {
      const status = e?.response?.status;
      if (status && status >= 400 && status !== 404) {
        showError(`Could not load events-capture config (${status})`);
      }
      const next = { ...DEFAULTS };
      setBaseline(next);
      setDraft(next);
    }
  }, [siteId, showError]);

  React.useEffect(() => { reload(); }, [reload]);

  const modifiedFields = React.useMemo(
    () => collectObjectDiff(baseline, draft, EVENTS_CAPTURE_DIFF_FIELDS),
    [baseline, draft],
  );
  const modifiedCount = modifiedFields.length;
  const dirty = modifiedCount > 0;

  const discard = React.useCallback(() => {
    if (baseline) setDraft(baseline);
  }, [baseline]);

  const patch = React.useCallback((p) => {
    if (!canConfigure) return;
    setDraft((prev) => ({ ...(prev || DEFAULTS), ...p }));
  }, [canConfigure]);

  const apply = React.useCallback(async () => {
    if (!siteId || !draft || !canConfigure) return;
    setIsApplying(true);
    try {
      const payload = {
        enabled:               !!draft.enabled,
        request_headers:       !!draft.request_headers,
        response_headers:      !!draft.response_headers,
        request_body_sample:   !!draft.request_body_sample,
        response_body_sample:  !!draft.response_body_sample,
        graphql_variables:     !!draft.graphql_variables,
        spec_actual_value:     !!draft.spec_actual_value,
        body_sample_cap_bytes: clampCap(draft.body_sample_cap_bytes),
      };
      await asec.eventsCaptureSet(siteId, payload);
      showSuccess("Saved");
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || String(e));
    } finally {
      setIsApplying(false);
    }
  }, [canConfigure, draft, reload, showError, showSuccess, siteId]);

  if (draft === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress size={28} sx={{ color: colors.primary[300] }} />
      </Box>
    );
  }

  const masterOn = !!draft.enabled;

  return (
    <Box sx={{ width: "100%", pb: dirty ? 10 : 0 }}>
      <Box sx={{ ...asecContentCardSx, borderRadius: "14px" }}>
        <Box sx={{ p: 3 }}>
          <SectionTitle mb={1}>Investigation depth</SectionTitle>
          <Typography sx={{
            fontSize: 14, lineHeight: "21px",
            color: colors.neutral[700],
            maxWidth: "66ch",
          }}>
            Choose which slices of the request and response pingora attaches to each API
            security event. Captured fields appear on the event detail drawer alongside
            identity, spec match, and threat-context cards.
          </Typography>
        </Box>

        <Callout />

        <MasterRow
          on={masterOn}
          onChange={(enabled) => patch({ enabled })}
          disabled={!canConfigure}
        />

        <CapturedFieldsGroup
          master={masterOn}
          fields={draft}
          patch={patch}
          disabled={!canConfigure}
        />

        {baseline?.updated_at ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 3.5, py: 2, borderTop: `1px solid ${colors.neutral[200]}` }}>
            <History size={14} color={colors.neutral[600]} />
            <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
              Last updated {formatDate(baseline.updated_at)}
            </Typography>
          </Stack>
        ) : null}
      </Box>

      <StickySaveBar
        open={dirty && canConfigure}
        modifiedCount={modifiedCount}
        modifiedFields={modifiedFields}
        onApply={apply}
        onDiscard={discard}
        isApplying={isApplying}
      />
    </Box>
  );
}

function clampCap(v) {
  const n = parseInt(v, 10);
  if (!Number.isFinite(n)) return CAP_DEFAULT;
  if (n < CAP_MIN) return CAP_MIN;
  if (n > CAP_MAX) return CAP_MAX;
  return n;
}

export default function AsecEventsCapture() {
  return (
    <AsecPage
      featureKey="api.openapi_validation"
      helpTooltip="Per-site capture toggles for request/response headers, body samples, GraphQL variables, and spec actual values. All captured data is PII-redacted at write time. Default off."
      title="Events capture"
      pageDescription="Configure which request and response slices pingora attaches to API security events. All captured data is PII-redacted at write time."
    >
      {(siteId) => <Body siteId={siteId} />}
    </AsecPage>
  );
}
